import { catchError, filter, first, map } from 'rxjs/operators';
import rfdc from 'rfdc';
import { TranslateService } from '@ngx-translate/core';
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as _ from 'lodash';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { select, Store } from '@ngrx/store';
import { Subject } from 'rxjs/internal/Subject';
import { Device } from '../_models';
import { environment } from '../../../environments/environment';
import { DeviceDataService } from './deviceData.service';
import { ChartMappingType } from '../../../assets/data/enum/chartMappingType';
import { AppState } from '../_reducers';
import { selectAllDevices } from '../_selectors/device.selectors';

export type SENSOR_TYPES = 'xsense' | 'xtrap' | 'xnode';

export interface DeviceTypeResponse {
  type: string;
  isNewUntil?: string | null;
  sorting: number;
}

@Injectable()
export class DeviceService {
  private clone = rfdc();

  private apiBaseUrl = environment.apiBaseUrl;
  private gatewayBaseUrl = environment.apiGatewayUrl;
  deviceList: Observable<Device[]>;
  avgResult: BehaviorSubject<any> = new BehaviorSubject<any>(0);
  avgChunckResult: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  senseAVGResult: Map<number, number> = new Map<number, number>();
  senseAVGChunkResult: Map<number, Array<number>> = new Map<number, Array<number>>();
  counterWeatherStation: number;
  counterSensors: number;
  deviceItemObj: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  textSearchDevice: BehaviorSubject<any> = new BehaviorSubject<any>(null);
  isXSense = false;
  isXTrap = false;
  isXNode = false;
  chartMappingType = ChartMappingType;
  private devicesSubject: Subject<Device[]> = new Subject<Device[]>();
  devices$: Observable<Device[]> = this.devicesSubject.asObservable();

  mappingUmidityByTpe: any = [
    {
      deviceType: 'X_NODE_SENTEK_30',
      params: ['sm1', 'sm2', 'sm3'],
    },
    {
      deviceType: 'X_NODE_SENTEK_60',
      params: ['sm1', 'sm2', 'sm3', 'sm4', 'sm5', 'sm6'],
    },
    {
      deviceType: 'X_NODE_SENTEK_90',
      params: ['sm1', 'sm2', 'sm3', 'sm4', 'sm5', 'sm6', 'sm7', 'sm8', 'sm9'],
    },
    {
      deviceType: 'X_NODE_SENTEK_30_TRISCAN',
      params: ['sm1', 'sm2', 'sm3'],
    },
    {
      deviceType: 'X_NODE_SENTEK_60_TRISCAN',
      params: ['sm1', 'sm2', 'sm3', 'sm4', 'sm5', 'sm6'],
    },
    {
      deviceType: 'X_NODE_SENTEK_90_TRISCAN',
      params: ['sm1', 'sm2', 'sm3', 'sm4', 'sm5', 'sm6', 'sm7', 'sm8', 'sm9'],
    },
    {
      deviceType: 'X_NODE_SOILWATCH_10_SINGLE',
      params: ['sm1'],
    },
    {
      deviceType: 'X_NODE_SOILWATCH_10_DOUBLE',
      params: ['sm1', 'sm2'],
    },
    {
      deviceType: 'X_NODE_TEROS_10_SINGLE',
      params: ['sm1'],
    },
    {
      deviceType: 'X_NODE_TEROS_10_DOUBLE',
      params: ['sm1', 'sm2'],
    },
    {
      deviceType: 'X_NODE_TEROS_12_SINGLE',
      params: ['sm1'],
    },
    {
      deviceType: 'X_NODE_TEROS_12_DOUBLE',
      params: ['sm1', 'sm2'],
    },
    {
      deviceType: 'X_NODE_TEROS_21_SINGLE',
      params: ['wp1'],
    },
    {
      deviceType: 'X_NODE_TEROS_21_DOUBLE',
      params: ['wp1', 'wp2'],
    },
    {
      deviceType: 'X_NODE_SOIL_EVO',
      params: ['sm1'],
    },
  ];

  constructor(
    private http: HttpClient,
    private store: Store<AppState>,
    private deviceDataService: DeviceDataService,
    private translate: TranslateService
  ) {
    this.deviceList = this.store.pipe(select(selectAllDevices));
  }

  getFromLocalStore(): Observable<Device[]> {
    return this.deviceList;
  }

  initDeviceItemObj() {
    this.deviceItemObj = new BehaviorSubject<any>(null);
  }

  getDeviceById = (id: number): Device => {
    let item: Device;
    this.deviceList
      .pipe(first((list: Device[]) => list && list.length > 0))
      .subscribe((list: Device[]) => {
        item = list.find((i) => i.id === id);
      });
    return item;
  };

  getDeviceByCode(code: string) {
    let item: Device;
    this.deviceList
      .pipe(first((list: Device[]) => list && list.length > 0))
      .subscribe((list: Device[]) => {
        item = list.find((i) => i.code === code);
      });
    return item;
  }

  getDeviceByCode$(code: string): Observable<Device> {
    return this.deviceList.pipe(
      first((list: Device[]) => list && list.length > 0),
      map((list: Device[]) => list.find((i) => i.code === code)),
      filter((device: Device | undefined): device is Device => !!device) // Filtra undefined
    );
  }

  getAvgBySensorIds = (sensors: any): any => {
    if (sensors && sensors.length > 0) {
      sensors.forEach((sensor) => {
        let deviceData = null;
        if (sensor && sensor.id) {
          // tslint:disable-next-line:prefer-const
          deviceData = this.getDeviceById(sensor.id);
          // tslint:disable-next-line:prefer-const
          const dateSelected = new Date();

          if (deviceData && deviceData.id) {
            this.mappingUmidityByTpe.forEach((item) => {
              if (item.deviceType === deviceData.deviceType) {
                this.deviceDataService
                  .getDeviceData(deviceData.code, dateSelected.toISOString(), '1d', null, false)
                  .subscribe((data) => {
                    // tslint:disable-next-line:prefer-const
                    const resultListByParam = [];
                    item.params.forEach((param) => {
                      // tslint:disable-next-line:prefer-const
                      const lastData = data[data.length - 1];
                      if (lastData && lastData[param]) {
                        resultListByParam.push(lastData[param]);
                      }
                    });

                    let meanTmp = _.mean(resultListByParam);
                    if (_.isNaN(meanTmp)) {
                      meanTmp = null;
                    }

                    /*NUOVO SVILUPPO */
                    let tmpChunk = [];
                    tmpChunk = _.chunk(resultListByParam, 3);
                    const arrayAvg = [];
                    tmpChunk.forEach((item) => {
                      arrayAvg.push(_.mean(item));
                    });

                    this.senseAVGChunkResult.set(deviceData.id, arrayAvg);
                    this.senseAVGResult.set(deviceData.id, meanTmp);
                    this.avgResult.next(this.senseAVGResult);
                    this.avgChunckResult.next(this.senseAVGChunkResult);
                    return meanTmp;
                  });
              }
            });
          }
        }
      });
    }
  };

  getListDevices = (addVirtual?: boolean, type = 'xSense') => {
    // tslint:disable-next-line:prefer-const
    let allDevices = [];
    this.deviceList.subscribe((devices) => {
      allDevices = devices;
      if (addVirtual && type == 'xSense') {
        let existsVirtual = false;
        allDevices.forEach((device) => {
          if (device.type === 'VIRTUAL') {
            existsVirtual = true;
          }
        });
        if (!existsVirtual) {
          const weatherStationVirtual = new Device();
          weatherStationVirtual.deviceType = 'VIRTUAL';
          weatherStationVirtual.type = 'VIRTUAL';
          weatherStationVirtual.description = 'Stazione Virtuale';
          allDevices.push(weatherStationVirtual);
        }
      } else if (addVirtual && type == 'xTrap') {
        let existsVirtual = false;
        allDevices.forEach((device: Device) => {
          if (device.type.valueOf().includes('VIRTUAL')) {
            existsVirtual = true;
          }
        });
        if (!existsVirtual) {
          const trap = new Device();
          trap.deviceType = 'VIRTUAL_XTRAP';
          trap.type = 'VIRTUAL_XTRAP';
          trap.description = 'Trappola Virtuale';
          allDevices.push(trap);
        }
      }
      this.devicesSubject.next(allDevices);
    });
    return allDevices;
  };

  getDevicesWeatherStationList = (addVirtual?: boolean) => {
    return this.getListDevices(addVirtual).filter(
      (obj) => obj.deviceType.startsWith('X_SENSE') || obj.deviceType === 'VIRTUAL'
    );
  };

  getDevicesSensorList = () => {
    return this.getListDevices().filter(
      (obj) =>
        obj.deviceType === 'X_NODE_SENTEK_30' ||
        obj.deviceType === 'X_NODE_SENTEK_60' ||
        obj.deviceType === 'X_NODE_SENTEK_90' ||
        obj.deviceType === 'X_NODE_SENTEK_30_TRISCAN' ||
        obj.deviceType === 'X_NODE_SENTEK_60_TRISCAN' ||
        obj.deviceType === 'X_NODE_SENTEK_90_TRISCAN' ||
        obj.deviceType === 'X_NODE_SOILWATCH_10_SINGLE' ||
        obj.deviceType === 'X_NODE_SOILWATCH_10_DOUBLE' ||
        obj.deviceType === 'X_NODE_TEROS_10_SINGLE' ||
        obj.deviceType === 'X_NODE_TEROS_10_DOUBLE' ||
        obj.deviceType === 'X_NODE_TEROS_12_SINGLE' ||
        obj.deviceType === 'X_NODE_TEROS_12_DOUBLE' ||
        obj.deviceType === 'X_NODE_TEROS_21_SINGLE' ||
        obj.deviceType === 'X_NODE_TEROS_21_DOUBLE'
    );
  };

  getAllDevicesSensorList = () => {
    return this.getListDevices().filter((obj) => obj.deviceType.startsWith('X_NODE'));
  };

  getDevicesXTrapList = (addVirtual?: boolean) => {
    return this.getListDevices(addVirtual, 'xTrap').filter(
      (obj) => obj.deviceType.includes('_TRAP') || obj.deviceType === 'VIRTUAL_XTRAP'
    );
  };

  // getDevicesWeatherStationListForDiseasesModule = () => {
  //     return this.getListDevices().filter(obj => obj.deviceType === 'X_SENSE_MINI_DAVIS'
  //     || obj.deviceType === 'X_SENSE_PRO_DAVIS'
  //     || obj.deviceType === 'X_SENSE_INDOOR'
  //     || obj.deviceType === 'X_SENSE_MINI_DAVIS_WITH_SOLAR_RADIATION'
  //     || obj.deviceType === 'X_SENSE_PRO_DAVIS_WITH_SOLAR_RADIATION'
  //     || obj.deviceType === 'X_SENSE_INDOOR_DAVIS_WITH_SOLAR_RADIATION'
  //     );
  // }

  getDevicesSensorListForDiseasesModule = () => {
    return this.getListDevices().filter((obj) => obj.deviceType === 'X_NODE_PHYTOS_31');
  };

  getDevices(): Observable<Device[]> {
    return this.http
      .get<any>(this.apiBaseUrl + 'api/private/v1/xsense/devices')
      .pipe(map((response) => response));
  }

  getXsenseType(): Observable<DeviceTypeResponse[]> {
    return this.http
      .get<
        DeviceTypeResponse[]
      >(this.apiBaseUrl + 'api/private/v1/xsense/deviceTypes/getXsenseType')
      .pipe(map((response) => response));
  }

  getXnodeType(): Observable<DeviceTypeResponse[]> {
    return this.http
      .get<DeviceTypeResponse[]>(this.apiBaseUrl + 'api/private/v1/xsense/deviceTypes/getXnodeType')
      .pipe(map((response) => response));
  }

  /**
   * Determines if a device type is "new" based on the isNewUntil date
   * @param isNewUntil The date string from the API or null
   * @returns true if the device is considered new (date is >= today), false otherwise
   */
  static isDeviceTypeNew(isNewUntil: string | null): boolean {
    if (!isNewUntil) {
      return false;
    }
    const today = new Date();
    const newUntilDate = new Date(isNewUntil);
    today.setHours(0, 0, 0, 0);
    newUntilDate.setHours(0, 0, 0, 0);
    return newUntilDate >= today;
  }

  getDevice(id: number): Observable<Device> {
    return this.http
      .get<any>(this.apiBaseUrl + 'api/private/v1/xsense/devices/' + id)
      .pipe(map((response) => response));
  }

  saveDevice(deviceItem): Observable<Device> {
    const device = { ...deviceItem };
    delete device.lastUpdated;
    delete device.dateCreated;
    if (device.farm) {
      const idfarm: any = {
        id: device.farm.id,
      };
      device.farm = idfarm;
    }

    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    device.companyId = companySelected ? companySelected.id : null;

    if (device.id) {
      delete device.farm;
      return this.http
        .put(this.apiBaseUrl + 'api/private/v1/xsense/devices/' + device.id, device)
        .pipe(map((response) => response));
    } else {
      return this.http
        .post(this.apiBaseUrl + 'api/private/v1/xsense/devices', device)
        .pipe(map((response) => response));
    }
  }

  deleteDevice(id: number): Observable<any> {
    return this.http.delete(this.apiBaseUrl + 'api/private/v1/xsense/devices/' + id).pipe(
      map(() => {
        return { id };
      })
    );
  }

  getParameters(deviceTypeId: number): Observable<any> {
    return this.http
      .get<any>(
        this.apiBaseUrl + 'api/private/v1/xsense/devices/getParameters?deviceId=' + deviceTypeId
      )
      .pipe(map((response) => response));
  }

  getConfigurationParametersByDeviceViewNameAndDeviceID = (viewName: string, id: number): any[] => {
    const device: Device = this.getDeviceById(id);
    if (device && device.id) {
      return device.configurationParameters?.filter((param) => param.viewName === viewName) ?? [];
    }
    return [];
  };

  setCounterWeatherStationAndCounterSensors(deviceList: Device[]) {
    const allWeatherStation = [];
    const allSensors = [];
    deviceList.forEach((device) => {
      if (device.deviceType.includes('X_SENSE')) {
        allWeatherStation.push(device);
      } else if (device.deviceType.includes('X_NODE')) {
        allSensors.push(device);
      }
    });
    this.counterWeatherStation = allWeatherStation.length;
    this.counterSensors = allSensors.length;
  }

  getCounterWeatherStation() {
    return this.counterWeatherStation;
  }

  getCounterSensors() {
    return this.counterSensors;
  }

  setTypeDeviceById(device: Device) {
    this.isXSense = false;
    this.isXNode = false;
    this.isXTrap = false;
    if (device && device.id) {
      if (device.deviceType.includes('X_SENSE')) {
        this.isXSense = true;
        return this.isXSense;
      } else if (device.deviceType.includes('X_NODE') && !device.deviceType.includes('_TRAP')) {
        this.isXNode = true;
        return this.isXNode;
      } else if (device.deviceType.includes('_TRAP')) {
        this.isXTrap = true;
        return this.isXTrap;
      }
    }
    return false;
  }

  isNode() {
    return this.isXNode;
  }

  isWeatherStation() {
    return this.isXSense;
  }

  isTrap() {
    return this.isXTrap;
  }

  getAllChartTypeFromMapping = (configurationParameters) => {
    return _.orderBy(
      configurationParameters.filter(
        (obj) => obj.viewName.substring(0, 6) === 'CHART_' && obj.viewName.slice(-8) !== '_PREVIEW'
      ),
      ['viewOrder', 'parameterName'],
      ['asc', 'asc']
    );
  };

  getAllChartPreviewTypeFromMapping = (configurationParameters) => {
    return _.orderBy(
      configurationParameters.filter(
        (obj) => obj.viewName.substring(0, 6) === 'CHART_' && obj.viewName.slice(-8) === '_PREVIEW'
      ),
      ['viewOrder', 'parameterName'],
      ['asc', 'asc']
    );
  };

  groupByViewName = (configurationParameters) => {
    // tslint:disable-next-line:prefer-const
    const groupByChartType = _.chain(
      _.orderBy(configurationParameters, ['viewOrder', 'parameterName'], ['asc', 'asc'])
    )
      .groupBy('viewName')
      .map((value, key) => ({ chart: key, parameterNames: value }))
      .value();

    //////////////////////// START CHART_MULTIPLE_LINE ////////////////////
    // tslint:disable-next-line:prefer-const
    const checkMultipleLine = groupByChartType.filter(
      (obj) =>
        obj.chart === this.chartMappingType.CHART_MULTIPLE_LINE ||
        obj.chart === this.chartMappingType.CHART_MULTIPLE_LINE_PREVIEW
    );

    if (checkMultipleLine && checkMultipleLine.length > 0) {
      // tslint:disable-next-line:prefer-const
      const keys = [];
      // tslint:disable-next-line:prefer-const
      const data = [];
      checkMultipleLine.forEach((element) => {
        if (element && element.parameterNames && element.parameterNames.length > 0) {
          // tslint:disable-next-line:prefer-const
          const finalarrayData = [];
          let payloadClone = null;
          element.parameterNames.forEach((param) => {
            if (param && param.parameterName) {
              // tslint:disable-next-line:prefer-const
              const split_string = param.parameterName.split(/(\d+)/);
              const prefix = split_string[0];
              if (prefix && !keys.includes(prefix)) {
                // tslint:disable-next-line:prefer-const
                payloadClone = this.clone(param);
                keys.push(prefix);
                // tslint:disable-next-line:prefer-const
                const dataByPrefix = element.parameterNames.filter(
                  (obj) => obj.parameterName.split(/(\d+)/)[0] === prefix
                );
                // tslint:disable-next-line:prefer-const
                const arrayData = [];
                if (dataByPrefix && dataByPrefix.length > 0) {
                  dataByPrefix.forEach((obj) => {
                    arrayData.push(obj.values);
                  });
                  // finalarrayData.push(this.getMultipleDataArray(arrayData, prefix))
                  data[prefix] = this.getMultipleDataArray(arrayData, prefix);
                }
                if (data[prefix] && data[prefix].length > 0) {
                  payloadClone.values = data[prefix];
                  payloadClone.parameterName = prefix;
                  finalarrayData.push(payloadClone);
                }
              }
            }
          });
          element.parameterNames = [];
          element.parameterNames = finalarrayData;
          groupByChartType.concat(element);
        }
      });
    }
    //////////////////////// END CHART_MULTIPLE_LINE ////////////////////

    //////////////////////// START CHART_MULTIPLE_WIND_DIRECTION ////////////////////
    // tslint:disable-next-line:prefer-const
    const checkMultipleWindLine = groupByChartType.filter(
      (obj) => obj.chart === this.chartMappingType.CHART_MULTIPLE_WIND_DIRECTION
    );

    if (checkMultipleWindLine && checkMultipleWindLine.length > 0) {
      // tslint:disable-next-line:prefer-const
      const data = [];
      checkMultipleWindLine.forEach((element) => {
        if (element && element.parameterNames && element.parameterNames.length > 0) {
          // tslint:disable-next-line:prefer-const
          const finalarrayData = [];
          let payloadClone = null;
          element.parameterNames.forEach((param) => {
            if (param && param.parameterName) {
              // tslint:disable-next-line:prefer-const
              if (param.parameterName === 'aws') {
                payloadClone = this.clone(param);
                data[0] = param.values;
              } else if (param.parameterName === 'awa') {
                data[1] = param.values;
              }
              payloadClone.values = [data[0], data[1]];
            }
          });

          finalarrayData.push(payloadClone);

          element.parameterNames = [];
          element.parameterNames = finalarrayData;
          groupByChartType.concat(element);
        }
      });
    }
    //////////////////////// END CHART_MULTIPLE_WIND_DIRECTION ////////////////////

    return groupByChartType;
  };

  getUnitByParameterName = (parameterName, configurationParameters) => {
    // tslint:disable-next-line:prefer-const
    const list: any = _.filter(configurationParameters, ['parameterName', parameterName]);
    return list && list.length > 0 ? list[0].parameterUnit : '';
  };

  getMultipleDataArray(inputArrays, nameString) {
    // tslint:disable-next-line:prefer-const
    const dataArray = [];
    let index = 1;
    // tslint:disable-next-line:prefer-const
    for (const current of inputArrays) {
      if (current != null && current.length > 0) {
        dataArray.push({ name: nameString + index, data: current });
      }
      index = index + 1;
    }
    return dataArray;
  }

  getAveragePerDay(dataPerDay: object) {
    for (const currentKey in dataPerDay) {
      const arrayTemp = dataPerDay[currentKey];
      const tempSum = arrayTemp.reduce((prev, curr) => {
        return (Number(prev) || 0) + (Number(curr) || 0);
      });
      const average = tempSum / arrayTemp.length;
      dataPerDay[currentKey] = average;
    }
    const resultArray = [];
    for (const currentKey in dataPerDay) {
      resultArray.push([new Date(currentKey).getTime(), dataPerDay[currentKey]]);
    }
    return resultArray;
  }

  isRequiredSensorForWeatherStation(deviceId): boolean {
    const device = this.getDeviceById(deviceId);
    if (device && device.id) {
      if (
        device.deviceType === 'X_SENSE_MINI_DAVIS' ||
        device.deviceType === 'X_SENSE_PRO_DAVIS' ||
        device.deviceType === 'X_SENSE_INDOOR' ||
        device.deviceType === 'X_SENSE_MINI_DAVIS_WITH_SOLAR_RADIATION' ||
        device.deviceType === 'X_SENSE_PRO_DAVIS_WITH_SOLAR_RADIATION' ||
        device.deviceType === 'X_SENSE_INDOOR_DAVIS_WITH_SOLAR_RADIATION'
      ) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  }

  getVirtualDevice(type: SENSOR_TYPES): Device {
    const item = new Device();
    switch (type) {
      case 'xtrap': {
        item.deviceType = 'VIRTUAL_XTRAP';
        item.type = 'VIRTUAL_XTRAP';
        item.description = this.translate.instant('xsense.type.VIRTUAL_XTRAP');
        break;
      }
      case 'xsense': {
        item.deviceType = 'VIRTUAL';
        item.type = 'VIRTUAL';
        item.description = this.translate.instant('xsense.type.VIRTUAL');
        break;
      }
    }
    return item;
  }

  checkDeviceDID(did: string): Observable<boolean> {
    return this.http
      .get<any>(this.gatewayBaseUrl + 'sensor-hub/v1/devices/check-did?did=' + did)
      .pipe(
        map((response) => response),
        catchError(() => {
          return of(false);
        })
      );
  }

  getDeviceType(did: string): Observable<any> {
    return this.http
      .get<any>(this.gatewayBaseUrl + 'sensor-hub/v1/devices/get-types?did=' + did)
      .pipe(
        map((response) => response),
        catchError(() => {
          return of(null);
        })
      );
  }

  //TODO This method is temporary for sensor 1.5.
  getDeviceWarning(did: string, farmId: number): Observable<string[]> {
    return this.http
      .get<any>(
        this.gatewayBaseUrl +
          'sensor-hub/v1/sensors/devices/get?deviceDid=' +
          did +
          '&farmId=' +
          farmId
      )
      .pipe(
        map((response) => this.setDeviceWarningList(response)),
        catchError(() => {
          return of([]);
        })
      );
  }

  setDeviceWarningList(device): string[] {
    if (!device) return [];
    const rules: Array<{ condition: boolean; message: string }> = [
      { condition: device.isClogAlert === true, message: 'rain_gauge_obstructed' },
      { condition: device.isTiltAlert === true, message: 'sensor_not_level' },
      { condition: device.isDataErrorAlert === true, message: 'sensor_not_sending_data' },
      {
        condition: typeof device.batteryLvl === 'number' && device.batteryLvl <= 2,
        message: 'low_battery',
      },
    ];

    return rules.filter((rule) => rule.condition).map((rule) => rule.message);
  }
}
